import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import decodeHtmlEntitiesRecursive from '@core/utils/helpers/decodeHtmlEntitiesRecursive'
import ApplyV2Button from '@core/components/ApplyDialogV2/ApplyV2Button'
import useFormattedDatesPeriod from '@core/utils/hooks/useFormattedDatesPeriod'
import ApplyV2Card from '@core/components/ApplyDialogV2/ApplyV2Card'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import ApplyV2CoursesStepAdd from '@core/components/ApplyDialogV2/ApplyV2CoursesStep/ApplyV2CoursesStepAdd'
import { useDeleteCourseV2ApplyMutation } from '@core/app/api/applyJobV2Api'
import ApplySkeleton from '@core/components/ApplyDialogV2/ApplySkeleton'
import ApplyV2CoursesStepEdit from '@core/components/ApplyDialogV2/ApplyV2CoursesStep/ApplyV2CoursesStepEdit'
import ApplyV2NoFieldsSwitch from '@core/components/ApplyDialogV2/ApplyV2NoFieldsSwitch'

import React from 'react'
import './ApplyV2CoursesStep.scss'

const baseClass = 'ApplyV2CoursesStep'

function ApplyV2CoursesStep() {
    const {
        postProfileData,
        postProfileError,
        isPostProfileLoading,
        isPostProfileSuccess,
        isSettingsLoading,
        isSettingsFetching,
        isFinalStep,
        setIsFinalStep,
        isPostApplied,
        onCloseApply,
        settingsData,
        navItems,
        onNextStep,
        postProfileRefetch,
    } = useApplyV2Context()
    const { getTitle } = useApplyV2NavTranslations()
    const [itemLoading, setItemLoading] = React.useState<string | number | null>(null)
    const [editState, setEditState] = React.useState<'add' | string | null>(null)
    const [isNoItems, setIsNoItems] = React.useState(false)
    const [deleteCourseRequest, params] = useDeleteCourseV2ApplyMutation()

    const course = postProfileData?.data?.course || []

    const handleDeleteCourseRequest = async (uid: string) => {
        try {
            setItemLoading(uid)
            await deleteCourseRequest({
                postUid: postProfileData?.data?.uid || '',
                courseUid: uid,
            }).unwrap()
            await postProfileRefetch()
        } catch (error) {
            console.log('Error deleting course:', error)
        } finally {
            setItemLoading(null)
        }
    }

    const getIsLoading = (uid?: string | number | null) => {
        return itemLoading === uid
    }

    const updatedCourse = course.find((c) => c.uid === editState || c.existingId === editState)

    if (editState === 'add') {
        return <ApplyV2CoursesStepAdd onClose={() => setEditState(null)} />
    }

    if (editState && editState.length > 3 && editState !== 'add' && updatedCourse) {
        return <ApplyV2CoursesStepEdit onClose={() => setEditState(null)} item={updatedCourse} />
    }

    return (
        <div className={baseClass}>
            <div className={`${baseClass}__container`}>
                {course.length === 0 ? (
                    <div className={`${baseClass}__items ${baseClass}__empty`}>
                        <div className={` ${baseClass}__empty--icon`}>
                            <svg
                                width="120"
                                height="120"
                                viewBox="0 0 120 120"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M60 108C88.7179 108 112 84.7638 112 56C112 27.2362 88.6154 4 60 4C31.2821 4 8 27.2362 8 56C8 84.7638 31.2821 108 60 108Z"
                                    fill="#F6F6F6"
                                    stroke="#C3C3C3"
                                    strokeMiterlimit="10"
                                />
                                <path
                                    d="M89.2314 91.0311H104.072C105.936 91.0311 107.534 89.4341 107.534 87.571V18.3693C107.534 16.5061 105.936 14.9092 104.072 14.9092H89.6922"
                                    fill="#EEEEEE"
                                />
                                <path
                                    d="M89.2314 91.0311H104.072C105.936 91.0311 107.534 89.4341 107.534 87.571V18.3693C107.534 16.5061 105.936 14.9092 104.072 14.9092H89.6922L89.2314 91.0311Z"
                                    stroke="#C3C3C3"
                                    strokeMiterlimit="10"
                                />
                                <path
                                    d="M30.3078 91.0311H15.9277C14.0636 91.0311 12.4658 89.4341 12.4658 87.571V18.3693C12.4658 16.5061 14.0636 14.9092 15.9277 14.9092H30.0116"
                                    fill="#EEEEEE"
                                />
                                <path
                                    d="M30.3078 91.0311H15.9277C14.0636 91.0311 12.4658 89.4341 12.4658 87.571V18.3693C12.4658 16.5061 14.0636 14.9092 15.9277 14.9092H30.0116L30.3078 91.0311Z"
                                    stroke="#C3C3C3"
                                    strokeMiterlimit="10"
                                />
                                <path
                                    d="M22.47 28.749H29.66"
                                    stroke="#C3C3C3"
                                    strokeMiterlimit="10"
                                    strokeLinecap="round"
                                />
                                <path
                                    d="M22.47 38.0654H29.66"
                                    stroke="#C3C3C3"
                                    strokeMiterlimit="10"
                                    strokeLinecap="round"
                                />
                                <path
                                    d="M22.47 47.6465H29.66"
                                    stroke="#C3C3C3"
                                    strokeMiterlimit="10"
                                    strokeLinecap="round"
                                />
                                <path
                                    d="M22.47 57.2285H29.66"
                                    stroke="#C3C3C3"
                                    strokeMiterlimit="10"
                                    strokeLinecap="round"
                                />
                                <path
                                    d="M22.47 75.8604H29.66"
                                    stroke="#C3C3C3"
                                    strokeMiterlimit="10"
                                    strokeLinecap="round"
                                />
                                <path d="M96.6159 28.749H89.6922" stroke="#C3C3C3" strokeMiterlimit="10" />
                                <path
                                    d="M96.6159 38.0654H89.6922"
                                    stroke="#C3C3C3"
                                    strokeMiterlimit="10"
                                    strokeLinecap="round"
                                />
                                <path
                                    d="M96.6159 47.6465H89.6922"
                                    stroke="#C3C3C3"
                                    strokeMiterlimit="10"
                                    strokeLinecap="round"
                                />
                                <path
                                    d="M96.6159 57.2285H89.6922"
                                    stroke="#C3C3C3"
                                    strokeMiterlimit="10"
                                    strokeLinecap="round"
                                />
                                <path
                                    d="M96.6159 75.8604H89.6922"
                                    stroke="#C3C3C3"
                                    strokeMiterlimit="10"
                                    strokeLinecap="round"
                                />
                                <path
                                    d="M86.2303 96.8866H33.5033C31.6393 96.8866 30.0415 95.2896 30.0415 93.4265V13.046C30.0415 11.1829 31.6393 9.58594 33.5033 9.58594H86.2303C88.0944 9.58594 89.6922 11.1829 89.6922 13.046V93.4265C89.6922 95.2896 88.0944 96.8866 86.2303 96.8866Z"
                                    fill="white"
                                    stroke="#C9C9C9"
                                    strokeMiterlimit="10"
                                />
                                <path
                                    d="M79.8391 35.6273H43.09C42.2911 35.6273 41.7585 35.1449 41.7585 34.4214V31.286C41.7585 30.5624 42.2911 30.0801 43.09 30.0801H79.8391C80.638 30.0801 81.1706 30.5624 81.1706 31.286V34.4214C81.1706 34.9037 80.638 35.6273 79.8391 35.6273Z"
                                    fill="#EEEEEE"
                                    stroke="#C3C3C3"
                                    strokeMiterlimit="10"
                                />
                                <path
                                    d="M79.8391 50.0003H43.09C42.2911 50.0003 41.7585 49.518 41.7585 48.7944V45.659C41.7585 44.9355 42.2911 44.4531 43.09 44.4531H79.8391C80.638 44.4531 81.1706 44.9355 81.1706 45.659V48.7944C81.1706 49.518 80.638 50.0003 79.8391 50.0003Z"
                                    fill="#EEEEEE"
                                    stroke="#C3C3C3"
                                    strokeMiterlimit="10"
                                />
                                <path
                                    d="M79.8391 64.639H43.09C42.2911 64.639 41.7585 64.1566 41.7585 63.4331V60.2977C41.7585 59.5742 42.2911 59.0918 43.09 59.0918H79.8391C80.638 59.0918 81.1706 59.5742 81.1706 60.2977V63.4331C81.1706 64.1566 80.638 64.639 79.8391 64.639Z"
                                    fill="#EEEEEE"
                                    stroke="#C3C3C3"
                                    strokeMiterlimit="10"
                                />
                                <path
                                    d="M79.8393 79.5433H65.193C64.3941 79.5433 63.8615 79.0609 63.8615 78.3374V75.202C63.8615 74.4785 64.3941 73.9961 65.193 73.9961H79.8393C80.6382 73.9961 81.1708 74.4785 81.1708 75.202V78.3374C81.1708 79.0609 80.6382 79.5433 79.8393 79.5433Z"
                                    fill="#EEEEEE"
                                    stroke="#C3C3C3"
                                    strokeMiterlimit="10"
                                />
                            </svg>
                        </div>
                        <div className={` ${baseClass}__empty--desc`}>
                            <div className={` ${baseClass}__empty--desc-title`}>
                                {isNoItems
                                    ? getTitle('10306', "I don't have any courses/certificates")
                                    : getTitle('10326', 'No courses / certificates added yet')}
                            </div>
                            <div className={` ${baseClass}__empty--desc-btns`}>
                                <ApplyV2Button
                                    variant={'outline'}
                                    size={'small'}
                                    className={` ${baseClass}__empty--desc-btns--add`}
                                    onClick={() => {
                                        setEditState('add')
                                    }}
                                    isDisabled={isNoItems}
                                    leftIcon={<i className="fa-regular fa-plus"></i>}
                                >
                                    {/*{getTitle('1533', 'Add course/certification')}*/}
                                    {getTitle('10325', 'Add items')}
                                </ApplyV2Button>
                                {/*<ApplyV2Switch*/}
                                {/*    checked={isNoItems}*/}
                                {/*    onChange={(e) => {*/}
                                {/*        setIsNoItems(e.target.checked)*/}
                                {/*        // if (e.target.checked) {*/}
                                {/*        //     setEditState(null)*/}
                                {/*        // } else {*/}
                                {/*        //     setEditState('add')*/}
                                {/*        // }*/}
                                {/*    }}*/}
                                {/*>*/}
                                {/*    {getTitle('9557', 'No items')}*/}
                                {/*</ApplyV2Switch>*/}
                                <ApplyV2NoFieldsSwitch
                                    className={`${baseClass}__empty--desc-switch`}
                                    checked={isNoItems}
                                    onChange={(v) => setIsNoItems(v)}
                                    label={getTitle('9557', 'No items')}
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className={`${baseClass}__items`}>
                        {course.map((item) => (
                            <ApplySkeleton
                                isLoading={getIsLoading(item.uid || item.existingId)}
                                key={item.uid ?? item.existingId}
                                style={{
                                    height: '150px',
                                    marginBottom: '1rem',
                                    display: 'block',
                                    width: '100%',
                                }}
                            >
                                <ApplyV2Card
                                    // key={item.uid ?? item.existingId}
                                    className={`${baseClass}__items--course`}
                                    title={decodeHtmlEntitiesRecursive(item.courseName)}
                                    files={item.files}
                                    onDelete={() => {
                                        handleDeleteCourseRequest(item.uid)
                                    }}
                                    onEdit={() => setEditState(item.uid)}
                                    date={{
                                        startDate: item.startDate,
                                        endDate: item.endDate,
                                        dateFormat: 'D MMM YYYY',
                                        showMonths: true,
                                        isShowPresent: false,
                                    }}
                                    // dateSection={
                                    //     (item.startDate || item.endDate) && (
                                    //         <div className={`${baseClass}__items--course-period`}>
                                    //             <span>
                                    //                 {formatDateRange(item.startDate, item.endDate, {
                                    //                     dateFormat: 'D MMM YYYY',
                                    //                 })}
                                    //             </span>
                                    //             {formatDuration(item.startDate, item.endDate, {
                                    //                 showMonths: true,
                                    //             }) && (
                                    //                 <span className={`${baseClass}__items--course-period-total`}>
                                    //                     {` (${formatDuration(item.startDate, item.endDate, {
                                    //                         showMonths: true,
                                    //                     })})`}
                                    //                 </span>
                                    //             )}
                                    //         </div>
                                    //     )
                                    // }
                                >
                                    {item.courseDescription}
                                </ApplyV2Card>
                            </ApplySkeleton>
                        ))}
                        {isPostProfileLoading && !itemLoading && (
                            <ApplySkeleton
                                isLoading={isPostProfileLoading}
                                style={{
                                    height: '150px',
                                    marginBottom: '1rem',
                                    display: 'block',
                                    width: '100%',
                                }}
                            />
                        )}
                        <ApplyV2Button
                            variant={'transparent'}
                            className={`${baseClass}__addBtn`}
                            onClick={() => {
                                setEditState('add')
                            }}
                            leftIcon={
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M7.99967 1.33301C8.36786 1.33301 8.66634 1.63148 8.66634 1.99967V7.12611H13.9997C14.3679 7.12611 14.6663 7.42459 14.6663 7.79278C14.6663 8.16097 14.3679 8.45944 13.9997 8.45944H8.66634V13.9997C8.66634 14.3679 8.36786 14.6663 7.99967 14.6663C7.63148 14.6663 7.33301 14.3679 7.33301 13.9997V8.45944H1.99967C1.63148 8.45944 1.33301 8.16097 1.33301 7.79278C1.33301 7.42459 1.63148 7.12611 1.99967 7.12611H7.33301V1.99967C7.33301 1.63148 7.63148 1.33301 7.99967 1.33301Z"
                                        fill="#19191A"
                                    />
                                </svg>
                            }
                        >
                            {getTitle('1533', 'Add course/certification')}
                        </ApplyV2Button>
                    </div>
                )}
            </div>
            <div className={`ApplyDialogV2__next ${baseClass}__next`}>
                {!settingsData?.steps?.course?.required && (
                    <ApplyV2Button
                        variant={'transparent'}
                        className={`ApplyDialogV2__next--btn ApplyDialogV2__next--btn-skip`}
                        rightIcon={'>'}
                        // isDisabled={!isValid || isLoading}
                        onClick={() => {
                            onNextStep()
                        }}
                    >
                        {getTitle('6107', 'Skip')}
                    </ApplyV2Button>
                )}
                <ApplyV2Button
                    variant={'primary'}
                    className={`ApplyDialogV2__next--btn ApplyDialogV2__next--btn-submit`}
                    onClick={onNextStep}
                    isLoading={isPostApplied || isPostProfileLoading}
                    isDisabled={isPostApplied || isPostProfileLoading || (!isNoItems && course.length === 0)}
                    type={'submit'}
                >
                    {getTitle('3035', 'Next')}
                </ApplyV2Button>
            </div>
        </div>
    )
}

export default ApplyV2CoursesStep
